import { useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { ArrowUpRight } from "lucide-react";

const initial = {
  full_name: "",
  email: "",
  phone: "",
  business_name: "",
  location_type: "Cafe",
  address: "",
  postcode: "",
  footfall: "",
  notes: "",
};

export default function LocationPartnerForm() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/leads/location-partner", form);
      toast.success("Request received.", {
        description: "Our partnerships team will reach out within 48 hours.",
      });
      setForm(initial);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        "Could not submit. Please check your details and try again.";
      toast.error("Submission failed", { description: String(detail) });
    } finally {
      setLoading(false);
    }
  };

  const input =
    "w-full bg-transparent border-b border-white/15 focus:border-[#5AB0FF] outline-none py-3 text-white placeholder:text-white/30 font-body transition-colors duration-200";
  const label = "font-mono-accent text-[10px] text-white/50 mb-1 block";

  return (
    <form
      data-testid="partner-form"
      onSubmit={submit}
      className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5"
    >
      <div className="md:col-span-1">
        <label className={label}>Full name</label>
        <input
          data-testid="partner-name"
          required
          value={form.full_name}
          onChange={upd("full_name")}
          className={input}
          placeholder="Jane Smith"
        />
      </div>
      <div>
        <label className={label}>Email</label>
        <input
          data-testid="partner-email"
          type="email"
          required
          value={form.email}
          onChange={upd("email")}
          className={input}
          placeholder="jane@business.co.uk"
        />
      </div>
      <div>
        <label className={label}>Phone</label>
        <input
          data-testid="partner-phone"
          required
          value={form.phone}
          onChange={upd("phone")}
          className={input}
          placeholder="07..."
        />
      </div>
      <div>
        <label className={label}>Business name</label>
        <input
          data-testid="partner-business"
          required
          value={form.business_name}
          onChange={upd("business_name")}
          className={input}
          placeholder="The Corner Cafe"
        />
      </div>
      <div>
        <label className={label}>Location type</label>
        <select
          data-testid="partner-location-type"
          value={form.location_type}
          onChange={upd("location_type")}
          className={`${input} appearance-none cursor-pointer`}
        >
          {[
            "Cafe",
            "Gym",
            "Office",
            "Student Hall",
            "Shopping Centre",
            "Hotel",
            "Cinema",
            "Other",
          ].map((o) => (
            <option key={o} value={o} className="bg-[#15161C]">
              {o}
            </option>
          ))}
        </select>
      </div>
      <div>
        <label className={label}>Postcode</label>
        <input
          data-testid="partner-postcode"
          required
          value={form.postcode}
          onChange={upd("postcode")}
          className={input}
          placeholder="SW1A 1AA"
        />
      </div>
      <div className="md:col-span-2">
        <label className={label}>Address</label>
        <input
          data-testid="partner-address"
          required
          value={form.address}
          onChange={upd("address")}
          className={input}
          placeholder="Street, City"
        />
      </div>
      <div className="md:col-span-2">
        <label className={label}>Daily footfall (est.)</label>
        <input
          data-testid="partner-footfall"
          value={form.footfall}
          onChange={upd("footfall")}
          className={input}
          placeholder="e.g. 500-1000"
        />
      </div>
      <div className="md:col-span-2">
        <label className={label}>Anything else?</label>
        <textarea
          data-testid="partner-notes"
          rows={2}
          value={form.notes}
          onChange={upd("notes")}
          className={`${input} resize-none`}
          placeholder="Opening hours, space available..."
        />
      </div>

      <div className="md:col-span-2 mt-2">
        <button
          type="submit"
          disabled={loading}
          data-testid="partner-submit"
          className="w-full group inline-flex items-center justify-between rounded-full bg-[#5AB0FF] px-6 py-4 text-sm font-semibold text-[#0A0B10] transition-transform duration-200 hover:-translate-y-0.5 neon-glow-cyan disabled:opacity-60 disabled:hover:translate-y-0"
        >
          {loading ? "Sending..." : "Request a machine · earn 15%"}
          <ArrowUpRight
            size={18}
            className="transition-transform duration-200 group-hover:rotate-45"
          />
        </button>
      </div>
    </form>
  );
}
